<template>
  <div class="guild-layout">
    <aside class="guild-sidebar">
      <div class="sidebar-brand">
        <div class="brand-mark compact"><span>S</span></div>
        <div><strong>StarLive</strong><small>GUILD CONSOLE</small></div>
      </div>
      <div class="guild-identity">
        <div class="guild-level">{{ account.guild.level }}</div>
        <div><strong>{{ account.guild.name }}</strong><small>{{ account.guild.code }}</small></div>
      </div>
      <nav>
        <button v-for="item in menu" :key="item.key" :class="{ active: active === item.key }" @click="active = item.key">
          <i :class="item.icon" /><span>{{ item.label }}</span><b v-if="item.badge">{{ item.badge }}</b>
        </button>
      </nav>
      <div class="sidebar-help">
        <i class="el-icon-headset" />
        <strong>需要平台协助？</strong>
        <span>通过站内信联系 StarLive 运营团队</span>
        <button @click="active='messages'">进入站内信</button>
      </div>
      <div class="sidebar-user">
        <div class="user-avatar">{{ account.name.slice(0, 1) }}</div>
        <div><strong>{{ account.name }}</strong><small>{{ roleText }}</small></div>
        <button title="修改密码" aria-label="修改密码" @click="passwordVisible=true"><i class="el-icon-key" /></button>
        <button title="退出登录" aria-label="退出登录" @click="$emit('logout')"><i class="el-icon-switch-button" /></button>
      </div>
    </aside>
    <section class="guild-main">
      <header class="guild-topbar">
        <div>
          <p>STARLIVE / {{ current.label.toUpperCase() }}</p>
          <h1>{{ current.label }}</h1>
        </div>
        <div class="topbar-right">
          <span class="period-chip"><i class="el-icon-date" /> {{ currentMonth }} 结算周期</span>
          <button class="icon-button" @click="refreshKey++"><i class="el-icon-refresh-right" /></button>
          <button class="message-button" @click="active='messages'"><i class="el-icon-bell" /><span v-if="unread">{{ unread }}</span></button>
        </div>
      </header>
      <main class="guild-content">
        <component :is="current.component" :key="`${active}-${refreshKey}`" v-bind="currentProps" @navigate="active=$event" @unread="unread=$event" />
      </main>
    </section>

    <el-dialog title="修改登录密码" :visible.sync="passwordVisible" width="480px" :close-on-click-modal="false" @closed="resetPasswordForm">
      <el-alert title="修改成功后，当前设备保持登录，其他设备将退出公会管理端。" type="warning" :closable="false" show-icon />
      <el-form label-position="top" class="password-form" @submit.native.prevent>
        <el-form-item label="当前密码"><el-input v-model="passwordForm.current_password" type="password" show-password autocomplete="current-password" maxlength="72" /></el-form-item>
        <el-form-item label="新密码">
          <el-input v-model="passwordForm.new_password" type="password" show-password autocomplete="new-password" maxlength="72" />
          <small class="password-hint">8–72 位，并同时包含字母和数字。</small>
        </el-form-item>
        <el-form-item label="再次输入新密码"><el-input v-model="passwordForm.confirm_password" type="password" show-password autocomplete="new-password" maxlength="72" @keyup.enter.native="submitPassword" /></el-form-item>
      </el-form>
      <span slot="footer"><el-button :disabled="passwordSaving" @click="passwordVisible=false">取消</el-button><el-button type="primary" :loading="passwordSaving" @click="submitPassword">确认修改</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import AnalyticsView from "../views/AnalyticsView.vue"
import FinanceView from "../views/FinanceView.vue"
import MembersView from "../views/MembersView.vue"
import MessagesView from "../views/MessagesView.vue"
import OverviewView from "../views/OverviewView.vue"
import RequestsView from "../views/RequestsView.vue"
import { passwordUpdate } from "../api/guild"

export default {
  name: "GuildShell",
  components: { AnalyticsView, FinanceView, MembersView, MessagesView, OverviewView, RequestsView },
  props: { account: { type: Object, required: true } },
  data: () => ({
    active: "overview",
    refreshKey: 0,
    unread: 0,
    passwordVisible: false,
    passwordSaving: false,
    passwordForm: { current_password: "", new_password: "", confirm_password: "" }
  }),
  computed: {
    menu() {
      return [
        { key: "overview", label: "经营概览", icon: "el-icon-data-analysis", component: "OverviewView" },
        { key: "members", label: "主播管理", icon: "el-icon-user", component: "MembersView" },
        { key: "requests", label: "申请与关系", icon: "el-icon-connection", component: "RequestsView" },
        { key: "analytics", label: "数据中心", icon: "el-icon-s-marketing", component: "AnalyticsView" },
        { key: "finance", label: "财务提现", icon: "el-icon-wallet", component: "FinanceView" },
        { key: "messages", label: "站内信", icon: "el-icon-chat-line-round", component: "MessagesView", badge: this.unread || "" }
      ]
    },
    current() { return this.menu.find(item => item.key === this.active) || this.menu[0] },
    currentProps() { return this.active === "members" ? { account: this.account } : {} },
    currentMonth() { return new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "2-digit" }).format(new Date()) },
    roleText() { return { owner: "公会负责人", manager: "公会运营", finance: "财务专员" }[this.account.role] || this.account.role }
  },
  methods: {
    resetPasswordForm() {
      this.passwordForm = { current_password: "", new_password: "", confirm_password: "" }
    },
    async submitPassword() {
      if (this.passwordSaving) return
      const { current_password: currentPassword, new_password: newPassword, confirm_password: confirmPassword } = this.passwordForm
      if (!currentPassword || !newPassword || !confirmPassword) {
        this.$message.warning("请完整填写当前密码和新密码")
        return
      }
      if (newPassword !== confirmPassword) {
        this.$message.warning("两次输入的新密码不一致")
        return
      }
      this.passwordSaving = true
      try {
        const res = await passwordUpdate(this.passwordForm)
        this.$message.success(res.msg === "AUTH.PASSWORD_UPDATED" ? "密码已修改，其他设备已退出登录" : res.msg)
        this.passwordVisible = false
      } finally {
        this.passwordSaving = false
      }
    }
  }
}
</script>
